import { Injectable } from '@angular/core';
import {
  AllTransactionType,
  CurrencyExchangeTransactionType,
  DocumentActionCategoryType,
  ExchangeCurrencyTransactionsResponse,
  ExchangeTransactionResponse,
  FileDocumentResponse,
  PaginatedResponse,
  ScheduledPaymentResponseType,
  TransactionType,
  ViewType,
} from '@finxone-platform/shared/sys-config-types';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { cloneDeep } from 'lodash';
import { Observable, catchError, tap, throwError } from 'rxjs';
import { AddProgressBarStack, RemoveProgressBarStack } from '../actions/progress-bar.action';
import {
  ClearExchangeTransactionDetails,
  ClearTransactionDetails,
  ClearTransactionReceiptFileData,
  ClearTransactionsCache,
  DeleteTransactionReceipt,
  GetExchangeTransactionById,
  GetScheduledTransactionDetailsById,
  GetTransactionById,
  GetTransactionReceiptFile,
  GetTransactionReceiptFileId,
  GetTransactions,
  ReplaceTransactionReceipt,
  SetLoadingStatus,
  UpdateTransactionReceiptDetails,
  UploadTransactionReceipt,
} from '../actions/transactions.action';
import { AccountService } from '../services/account-service/account-service.service';
import {
  TransactionDetailResponse,
  TransactionInfoType,
  TransactionStateType,
} from '../services/account-service/account.type';
import { ConfigService } from '../services/config-service/config-service.service';
import { DocumentService } from '../services/document-service/document.service';
import { ExchangeService } from '../services/exchange-service/exchange-service.service';
import { TransactionService } from '../services/transaction-service/transaction.service';
import { ApiActions, UpdateApiIsLoadingAction } from './api-loading.state';

@State<TransactionStateType>({
  name: 'transactions',
  defaults: {
    isLoading: false,
    accounts: {},
    currentExchangeTransactionDetail: {} as ExchangeTransactionResponse,
    currentTransactionId: '',
    currentTransactionType: '',
    currentTransactionDetails: {} as TransactionDetailResponse,
    transactionReceiptDetails: [] as FileDocumentResponse[],
    pendingTransactionDetails: null,
    pendingTransactions: [],
    isFileUploading: false,
    isFileDownloading: false,
    isDocumentDisplayDownloading: false,
    transactionReceiptBlobUrl: '',
    currentTransactionNumber: '',
  },
})
@Injectable()
export class TransactionState {
  constructor(
    private configService: ConfigService,
    private accountService: AccountService,
    private exchangeService: ExchangeService,
    private transactionService: TransactionService,
    private documentService: DocumentService,
  ) {}

  @Selector()
  static getTransactions(state: TransactionStateType) {
    return state;
  }

  @Selector()
  static getTransactionDocuments(state: TransactionStateType) {
    return state?.transactionReceiptDetails;
  }

  @Selector()
  static getLoadingStatus(state: TransactionStateType) {
    return state.isLoading;
  }

  @Selector()
  static getPendingTransactions(state: TransactionStateType) {
    return state.pendingTransactions;
  }

  @Selector()
  static getCurrentPendingTransaction(state: TransactionStateType) {
    return state.pendingTransactionDetails;
  }

  @Action(SetLoadingStatus)
  setLoadingStatus(ctx: StateContext<TransactionStateType>, action: SetLoadingStatus) {
    try {
      ctx.patchState({
        ...ctx.getState(),
        isLoading: action.isLoading,
      });
      return true;
    } catch (err) {
      return throwError(() => err);
    }
  }

  @Action(GetTransactions)
  fetchTransactions(ctx: StateContext<TransactionStateType>, action: GetTransactions) {
    try {
      if (action.isLoaderShown) {
        ctx.dispatch(new AddProgressBarStack({ uniqueId: 'GetTransactions' }));
        ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.getTransactions, true));
      }
      const { params } = action;
      const transactionType = params.type;
      const state = ctx.getState();
      const accounts = state.accounts ?? {};
      const account = accounts[action.accountId] ?? structuredClone(defaultAccount);
      account[transactionType] = account[transactionType] ?? {
        result: [],
        totalPages: 1,
        totalItems: 0,
        limit: 10,
        page: 1,
      };

      // clone prev data in case the page is not 1
      const prevData = cloneDeep(account[transactionType].result);
      // clear state before fetching new data
      // James 06-12-2025: Only clear state if workflow config is active
      // Need to investigate why this is needed for workflow mode
      if (this.configService.getActiveConfigType().isWorkflow) {
        ctx.patchState({
          isLoading: false,
          accounts: {},
        });
      }

      return this.accountService.getTransactionSummary(action.accountId, params).pipe(
        tap(
          (
            transactionList: PaginatedResponse<
              TransactionInfoType | ExchangeCurrencyTransactionsResponse | ScheduledPaymentResponseType
            >,
          ) => {
            ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'GetTransactions' }));

            // Initialize or update the transaction type data
            let transResult;
            if (action.txsViewType === ViewType.TABLE) {
              transResult = transactionList.result;
            } else {
              if (params.page === 1) {
                transResult = transactionList.result;
              } else {
                transResult = prevData.concat(transactionList.result);
              }
            }
            const response = {
              result: transResult,
              totalPages: transactionList.totalPages,
              totalItems: transactionList.totalItems,
              limit: params.size,
              page: transactionList.page,
            };
            ctx.patchState({
              accounts: {
                ...accounts,
                [action.accountId]: {
                  ...account,
                  [transactionType]: response,
                },
              },
              isLoading: false,
            });
            ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.getTransactions, false));
          },
        ),
        catchError<unknown, Observable<boolean>>((_err) => {
          ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.getTransactions, false));
          ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'GetTransactions' }));
          throw _err;
        }),
      );
    } catch (err) {
      ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.getTransactions, false));
      ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'GetTransactions' }));
      return throwError(() => err);
    }
  }

  @Action(ClearTransactionsCache)
  clearAccountCache(ctx: StateContext<TransactionStateType>) {
    ctx.patchState({
      isLoading: false,
      accounts: {},
    });
  }

  @Action(GetExchangeTransactionById)
  getExchangeTransactionById(ctx: StateContext<TransactionStateType>, action: GetExchangeTransactionById) {
    ctx.patchState({
      ...ctx.getState(),
      currentTransactionId: action.exchangeTransactionId,
      currentTransactionType: action.transactionType,
    });
    return this.exchangeService.getExchangeDetail(action.exchangeTransactionId).pipe(
      tap((response) => {
        ctx.patchState({
          ...ctx.getState(),
          currentExchangeTransactionDetail: response,
        });
      }),
      catchError((_err) => {
        throw _err;
      }),
    );
  }

  @Action(ClearExchangeTransactionDetails)
  clearExchangeTransactionDetails(ctx: StateContext<TransactionStateType>) {
    ctx.patchState({
      ...ctx.getState(),
      currentExchangeTransactionDetail: {} as ExchangeTransactionResponse,
    });
  }

  @Action(GetTransactionById)
  getTransactionById(ctx: StateContext<TransactionStateType>, action: GetTransactionById) {
    return this.transactionService
      .getTransactionDetailById(
        action.currentTransactionNumber,
        action.transactionId,
        action.transactionType as TransactionType,
        action.accountId,
      )
      .pipe(
        tap((response: TransactionDetailResponse) => {
          ctx.patchState({
            ...ctx.getState(),
            currentTransactionDetails: response,
            currentTransactionId: response.id,
            currentTransactionType: action.transactionType,
            currentTransactionNumber: action.currentTransactionNumber,
          });
        }),
        catchError((error) => {
          throw error;
        }),
      );
  }

  @Action(GetScheduledTransactionDetailsById)
  getScheduledTransactionDetailsById(
    ctx: StateContext<TransactionStateType>,
    action: GetScheduledTransactionDetailsById,
  ) {
    return this.transactionService.getScheduledTransactionDetails(action.scheduledTransactionId).pipe(
      tap((response: TransactionDetailResponse) => {
        ctx.patchState({
          ...ctx.getState(),
          currentTransactionDetails: response,
          currentTransactionId: response.id,
          currentTransactionType: action.transactionType,
        });
      }),
      catchError((error) => {
        throw error;
      }),
    );
  }

  @Action(ClearTransactionDetails)
  clearTransactionDetails(ctx: StateContext<TransactionStateType>) {
    ctx.patchState({
      ...ctx.getState(),
      currentTransactionDetails: {} as TransactionDetailResponse,
    });
  }

  @Action(DeleteTransactionReceipt)
  deleteTransactionReceipt(ctx: StateContext<TransactionStateType>, action: DeleteTransactionReceipt) {
    return this.documentService.deleteDocument(action.type, action.transactionId, action.documentId).pipe(
      tap(() => {
        ctx.patchState({
          ...ctx.getState(),
          transactionReceiptBlobUrl: '',
        });
      }),
      catchError((error) => {
        throw error;
      }),
    );
  }

  @Action(UploadTransactionReceipt)
  uploadTransactionReceipt(ctx: StateContext<TransactionStateType>, action: UploadTransactionReceipt) {
    ctx.patchState({
      ...ctx.getState(),
      isFileUploading: true,
    });
    return this.documentService.uploadDocument(action.payload).pipe(
      tap((response) => {
        ctx.patchState({
          ...ctx.getState(),
          transactionReceiptDetails: response,
          transactionReceiptBlobUrl: response?.[0]?.path,
          isFileUploading: false,
        });
      }),
      catchError((error) => {
        ctx.patchState({
          ...ctx.getState(),
          isFileUploading: false,
        });
        return throwError(() => error);
      }),
    );
  }

  @Action(ReplaceTransactionReceipt)
  replaceTransactionReceipt(ctx: StateContext<TransactionStateType>, action: ReplaceTransactionReceipt) {
    ctx.patchState({
      ...ctx.getState(),
      isFileUploading: true,
    });
    return this.documentService.replaceDocument(action.payload, action.type, action.transactionId).pipe(
      tap((response) => {
        ctx.patchState({
          ...ctx.getState(),
          transactionReceiptDetails: response,
          transactionReceiptBlobUrl: response?.[0]?.path,
          isFileUploading: false,
        });
      }),
      catchError((error) => {
        ctx.patchState({
          ...ctx.getState(),
          isFileUploading: false,
        });
        return throwError(() => error);
      }),
    );
  }

  @Action(UpdateTransactionReceiptDetails)
  updateTransactionReceiptDetails(
    ctx: StateContext<TransactionStateType>,
    action: UpdateTransactionReceiptDetails,
  ) {
    ctx.patchState({
      ...ctx.getState(),
      transactionReceiptDetails: action.fileDocumentResponse,
    });
  }

  @Action(GetTransactionReceiptFileId)
  getTransactionReceiptFileId(ctx: StateContext<TransactionStateType>, action: GetTransactionReceiptFileId) {
    ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.documentsList, true));
    return this.documentService
      .getDocumentFiles(
        action.type,
        action.transactionId,
        action?.documentSubCategory ?? DocumentActionCategoryType.RECEIPTS,
      )
      .pipe(
        tap((response) => {
          ctx.patchState({
            ...ctx.getState(),
            transactionReceiptDetails: response,
          });
          ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.documentsList, false));
        }),
        catchError<unknown, Observable<boolean>>((_err) => {
          ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.documentsList, false));
          throw _err;
        }),
      );
  }

  @Action(ClearTransactionReceiptFileData)
  clearTransactionReceiptFileData(ctx: StateContext<TransactionStateType>) {
    ctx.patchState({
      transactionReceiptDetails: [],
      isFileUploading: false,
      transactionReceiptBlobUrl: '',
      isFileDownloading: false,
      isDocumentDisplayDownloading: false,
    });
  }

  @Action(GetTransactionReceiptFile)
  getTransactionReceiptFile(ctx: StateContext<TransactionStateType>, action: GetTransactionReceiptFile) {
    switch (action.viewType) {
      case 'download':
        ctx.patchState({ isFileDownloading: true });
        break;
      case 'display':
        ctx.patchState({ isDocumentDisplayDownloading: true });
        break;
    }

    return this.documentService.getFile(action.type, action.transactionId).pipe(
      tap((response) => {
        ctx.patchState({
          transactionReceiptBlobUrl: response?.[0]?.path,
          isFileDownloading: false,
          isDocumentDisplayDownloading: false,
        });
      }),
      catchError((error) => {
        ctx.patchState({
          isFileDownloading: false,
          isDocumentDisplayDownloading: false,
        });
        throw error;
      }),
    );
  }
}

const defaultAccount: {
  [txType in AllTransactionType]: {
    result: (TransactionInfoType | ExchangeCurrencyTransactionsResponse | ScheduledPaymentResponseType)[];
    totalPages: number;
    totalItems: number;
    limit: number;
    page: number;
  };
} = {
  [TransactionType.INCOMING]: {
    result: [],
    totalPages: 1,
    totalItems: 0,
    page: 1,
    limit: 10,
  },
  [TransactionType.OUTGOING]: {
    result: [],
    totalPages: 1,
    totalItems: 0,
    page: 1,
    limit: 10,
  },
  [TransactionType.ALL]: {
    result: [],
    totalPages: 1,
    totalItems: 0,
    page: 1,
    limit: 10,
  },
  [TransactionType.SCHEDULED]: {
    result: [],
    totalPages: 1,
    totalItems: 0,
    page: 1,
    limit: 10,
  },
  [TransactionType.PENDING_WITHDRAWAL]: {
    result: [],
    totalPages: 1,
    totalItems: 0,
    page: 1,
    limit: 10,
  },
  [CurrencyExchangeTransactionType.CURRENCYINCOMING]: {
    result: [],
    totalPages: 1,
    totalItems: 0,
    page: 1,
    limit: 10,
  },
  [CurrencyExchangeTransactionType.CURRENCYOUTGOING]: {
    result: [],
    totalPages: 1,
    totalItems: 0,
    page: 1,
    limit: 10,
  },
  [CurrencyExchangeTransactionType.CURRENCYALL]: {
    result: [],
    totalPages: 1,
    totalItems: 0,
    page: 1,
    limit: 10,
  },
};
